import { useEffect, useMemo, useState } from 'react';

import { useAttendanceState } from '@/context/AttendanceContext';
import { useServiceCatalogState } from '@/context/ServiceContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { useVisitorState } from '@/context/VisitorContext';
import { fetchHomeDashboardData } from '@/lib/api';
import { buildHomeDashboardData } from '@/lib/home-dashboard';
import type { HomeDashboardData } from '@/lib/types';

type HomeDashboardState = {
  data: HomeDashboardData;
  isLoading: boolean;
  isFromApi: boolean;
  error: string | null;
  refresh: () => void;
};

export function useHomeDashboard(): HomeDashboardState {
  const { staffMembers } = useStaffDirectoryState();
  const { services } = useServiceCatalogState();
  const { sessions } = useAttendanceState();
  const { visitors } = useVisitorState();
  const [apiData, setApiData] = useState<HomeDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const localData = useMemo(
    () => buildHomeDashboardData({ staffMembers, services, visitors, sessions }),
    [services, sessions, staffMembers, visitors]
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setIsLoading(true);

    fetchHomeDashboardData(controller.signal)
      .then((payload) => {
        if (!active) {
          return;
        }

        setApiData(payload);
        setError(null);
      })
      .catch((caught) => {
        if (!active || controller.signal.aborted) {
          return;
        }

        setApiData(null);
        setError(caught instanceof Error ? caught.message : 'Unable to load dashboard data.');
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [refreshKey]);

  return {
    data: apiData ?? localData,
    isLoading: isLoading && !apiData,
    isFromApi: Boolean(apiData),
    error,
    refresh: () => setRefreshKey((current) => current + 1),
  };
}
